import type { Booking, Closure, TimeRange, Venue } from "./contracts";

export const tokyoDay = (value: string) =>
  new Date(Date.parse(value) + 9 * 3_600_000).toISOString().slice(0, 10);
export const dayMinute = (day: string, minutes: number) =>
  new Date(
    Date.parse(`${day}T00:00:00+09:00`) + minutes * 60_000,
  ).toISOString();
export const rangesOverlap = (a: TimeRange, b: TimeRange) =>
  Date.parse(a.start) < Date.parse(b.end) &&
  Date.parse(a.end) > Date.parse(b.start);
export const bookingBlock = (
  booking: Booking,
  gapMinutes: number,
): TimeRange => ({
  start: new Date(
    Date.parse(booking.occupiedStart) - gapMinutes * 60_000,
  ).toISOString(),
  end: new Date(
    Date.parse(booking.occupiedEnd) + gapMinutes * 60_000,
  ).toISOString(),
});
export function availableVenueRanges(
  venue: Venue,
  day: string,
  bookings: Booking[],
  closures: Closure[],
  now: number,
): TimeRange[] {
  const end = Date.parse(dayMinute(day, venue.closeHour * 60));
  let cursor = Math.max(
    Date.parse(dayMinute(day, venue.openHour * 60)),
    Math.floor(now / 60_000) * 60_000 + 60_000,
  );
  if (cursor >= end) return [];
  const blocks = [
    ...bookings.map((b) => bookingBlock(b, venue.gapMinutes)),
    ...closures,
  ]
    .map((b) => ({ start: Date.parse(b.start), end: Date.parse(b.end) }))
    .filter((b) => b.end > cursor && b.start < end)
    .sort((a, b) => a.start - b.start);
  const free: TimeRange[] = [];
  for (const b of blocks) {
    if (b.start > cursor)
      free.push({
        start: new Date(cursor).toISOString(),
        end: new Date(Math.min(b.start, end)).toISOString(),
      });
    cursor = Math.max(cursor, b.end);
    if (cursor >= end) break;
  }
  if (cursor < end)
    free.push({
      start: new Date(cursor).toISOString(),
      end: new Date(end).toISOString(),
    });
  return free;
}
export const rangeFits = (ranges: TimeRange[], value: TimeRange) =>
  Date.parse(value.end) > Date.parse(value.start) &&
  ranges.some(
    (r) =>
      Date.parse(value.start) >= Date.parse(r.start) &&
      Date.parse(value.end) <= Date.parse(r.end),
  );
export function availableStarts(
  ranges: TimeRange[],
  durationMinutes: number,
): TimeRange[] {
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return [];
  const duration = durationMinutes * 60_000,
    step = 15 * 60_000;
  const starts = new Set<number>();
  for (const range of ranges) {
    const first = Date.parse(range.start),
      last = Date.parse(range.end) - duration;
    if (last < first) continue;
    starts.add(first);
    starts.add(last);
    for (
      let start = Math.ceil(first / step) * step;
      start <= last;
      start += step
    )
      starts.add(start);
  }
  return [...starts]
    .sort((a, b) => a - b)
    .map((start) => ({
      start: new Date(start).toISOString(),
      end: new Date(start + duration).toISOString(),
    }));
}

export function alignVenueDeadlines(
  old: {
    choiceDeadline: string;
    decisionDeadline: string;
    registrationDeadline: string;
  },
  earliestStart: string,
  poll: boolean,
) {
  const earliest = Date.parse(earliestStart);
  let decision = Date.parse(old.decisionDeadline),
    choice = Date.parse(old.choiceDeadline);
  if (poll) {
    if (!Number.isFinite(decision) || decision >= earliest)
      decision = earliest - 3_600_000;
    if (!Number.isFinite(choice) || choice >= decision)
      choice = decision - 3_600_000;
  }
  let registration = Date.parse(old.registrationDeadline);
  if (
    !Number.isFinite(registration) ||
    registration >= earliest ||
    (poll && registration <= decision)
  )
    registration = earliest - 60_000;
  return {
    choiceDeadline:
      poll && choice !== Date.parse(old.choiceDeadline)
        ? new Date(choice).toISOString()
        : old.choiceDeadline,
    decisionDeadline:
      poll && decision !== Date.parse(old.decisionDeadline)
        ? new Date(decision).toISOString()
        : old.decisionDeadline,
    registrationDeadline:
      registration !== Date.parse(old.registrationDeadline)
        ? new Date(registration).toISOString()
        : old.registrationDeadline,
  };
}
